import { useContext, useEffect, useState } from "react";
import {
  Modal,
  Tag,
  Button,
  Input,
  Divider,
  Row,
  Col,
  Alert,
  Timeline,
  Descriptions,
  Typography,
} from "antd";
import {
  CheckCircleFilled,
  ClockCircleFilled,
  CloseCircleFilled,
  SyncOutlined,
  FilePdfOutlined,
} from "@ant-design/icons";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import dayjs from "dayjs";
import axios from "axios";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { AuthContext } from "../../context/AuthContext";

const { Text, Title } = Typography;

const TAG_COLOR = {
  Confirm: "success",
  Pending: "warning",
  Separated: "error",
  Extend: "processing",
};

const TAG_LABEL = {
  Confirm: "Confirmed",
  Pending: "Pending",
  Separated: "Separated",
  Extend: "Extended",
};

function StepIcon({ status, isCurrentStep }) {
  if (isCurrentStep)
    return <ClockCircleFilled style={{ color: "#0e497b", fontSize: 15 }} />;
  if (status === "Confirm")
    return <CheckCircleFilled style={{ color: "#52c41a", fontSize: 15 }} />;
  if (status === "Separated")
    return <CloseCircleFilled style={{ color: "#ff4d4f", fontSize: 15 }} />;
  if (status === "Extend")
    return <SyncOutlined style={{ color: "#1890ff", fontSize: 15 }} />;
  return <ClockCircleFilled style={{ color: "#d9d9d9", fontSize: 15 }} />;
}



export default function ProbationReviewModal({ visible, employee, onClose }) {
  const { authData, backUrl } = useContext(AuthContext);

  const [remarks, setRemarks] = useState("");
  const [newProbationDate, setNewProbationDate] = useState(null);
  const [extendModalVisible, setExtendModalVisible] = useState(false);

  const [confirmLoading, setConfirmLoading] = useState(false);
  const [separateLoading, setSeparateLoading] = useState(false);
  const [submitExtendLoading, setSubmitExtendLoading] = useState(false);
  const [pdfLoading, setPdfLoading] = useState(false);

  const [activeStepData, setActiveStepData] = useState(null);
  const [canEdit, setCanEdit] = useState(false);
  const [allSteps, setAllSteps] = useState([]);

  const isSuperuser = authData?.employee?.is_superuser;
  const isHR = authData?.employee?.is_hr;
  const myEmployeeId = authData?.employee?.id;

  useEffect(() => {
    if (!employee) return;

    // Trim statuses to handle potential data inconsistencies (trailing spaces)
    const steps = (employee.approval_steps || []).map(s => ({
      ...s,
      status: (s.status || "").trim()
    }));
    setAllSteps(steps);
    setRemarks("");
    setNewProbationDate(null);

    // Use backend's is_current_step flag as the authoritative source
    // is_current_step = true means it's this person's turn right now
    // We filter for is_current_step to ensure we pick the right step in a multi-cycle history
    const myStep = steps.find((s) => s.approver === myEmployeeId && s.is_current_step);
    const currentStep = steps.find((s) => s.is_current_step === true);

    let active = null;

    if (myStep) {
      // It's my turn
      active = myStep;
    } else if (isSuperuser && currentStep) {
      // Superuser can act on whoever's turn it currently is
      active = currentStep;
    }

    setActiveStepData(active);
    setCanEdit(active !== null);

    // Pre-fill remarks if already submitted
    if (myStep?.remarks) setRemarks(myStep.remarks);
  }, [employee, myEmployeeId, isSuperuser]);

  if (!employee) return null;
  const emp = employee.employee;

  const handleDecision = async (actionType) => {
    if (!activeStepData) {
      toast.error("No active approval step found for your account.");
      return;
    }
    if (!remarks.trim()) {
      toast.warning("Please enter your remarks before submitting.");
      return;
    }
    if (actionType === "Extend") {
      setExtendModalVisible(true);
      return;
    }

    try {
      if (actionType === "Confirm") setConfirmLoading(true);
      if (actionType === "Separated") setSeparateLoading(true);

      await axios.post(
        `${backUrl}/api/probation/probation-approvals/${activeStepData.id}/submit-decision/`,
        { action: actionType, remarks },
        { headers: { Authorization: `Bearer ${authData?.access}` } }
      );

      toast.success(`Decision "${actionType}" submitted successfully.`);
      onClose();
    } catch (err) {
      const msg =
        err?.response?.data?.detail ||
        err?.response?.data?.error ||
        err?.response?.data?.message ||
        "Something went wrong.";
      toast.error(msg);
    } finally {
      setConfirmLoading(false);
      setSeparateLoading(false);
    }
  };

  const handleExtendSubmit = async () => {
    if (!newProbationDate) { toast.warning("Please select an extension date."); return; }
    if (!remarks.trim()) { toast.warning("Remarks are required."); return; }
    if (!activeStepData) { toast.error("No active approval step found."); return; }

    try {
      setSubmitExtendLoading(true);
      await axios.post(
        `${backUrl}/api/probation/probation-approvals/${activeStepData.id}/submit-decision/`,
        {
          action: "Extend",
          remarks,
          new_probation_end_date: dayjs(newProbationDate).format("YYYY-MM-DD"),
        },
        { headers: { Authorization: `Bearer ${authData?.access}` } }
      );
      toast.success(
        `Probation extended to ${dayjs(newProbationDate).format("DD/MM/YYYY")}`
      );
      setExtendModalVisible(false);
      onClose();
    } catch (err) {
      toast.error(
        err?.response?.data?.detail ||
        err?.response?.data?.error ||
        "Failed to extend probation."
      );
    } finally {
      setSubmitExtendLoading(false);
    }
  };

  const dueDaysColor =
    employee.due_days == null ? "default"
      : employee.due_days < 0 ? "error"
        : employee.due_days <= 7 ? "warning"
          : "success";

  const dueDaysLabel =
    employee.due_days == null
      ? "—"
      : employee.due_days < 0
        ? `${Math.abs(employee.due_days)} days overdue`
        : `${employee.due_days} days left`;

  // Superuser acting on a step that's not their own
  const isAdminAction = isSuperuser && activeStepData && activeStepData.approver !== myEmployeeId;

  // Re-review cycle detection: final_remarks present + all steps Pending = record was extended
  const isReReviewCycle =
    !!employee.final_remarks &&
    allSteps.length > 0 &&
    allSteps.every((s) => s.status === "Pending");

  // TeamLeaves-style timeline: name, designation, status tag, date, remarks, extended-to date
  const timelineItems = allSteps.map((step) => {
    const isCurrentStep = step.is_current_step === true;
    const isCompleted = step.status !== "Pending";
    const isNotReached = step.status === "Pending" && !isCurrentStep;

    const isHalted = step.remarks && (step.remarks.includes("Process halted") || step.remarks.includes("prior approver"));

    const timelineColor =
      step.status === "Separated" ? "red"
        : step.status === "Confirm" ? "green"
          : step.status === "Extend" ? (isHalted ? "#8c8c8c" : "#1890ff")
            : isCurrentStep ? "#722ed1"
              : "gray";

    return {
      color: timelineColor,
      children: (
        <div style={{ marginBottom: 10, opacity: (isNotReached || isHalted) ? 0.5 : 1 }}>

          {/* Row 1: Name + "You" tag + Status tag */}
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 8 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
              <Text strong style={{ fontSize: 14 }}>
                {step.approver_name}
              </Text>
              {step.approver === myEmployeeId && (
                <Tag color="blue" style={{ fontSize: 10, padding: "0 5px", lineHeight: "18px" }}>You</Tag>
              )}
            </div>
            {isCurrentStep ? (
              <Tag color="purple" style={{ margin: 0 }}>Awaiting Approval</Tag>
            ) : isNotReached ? (
              <Tag color="default" style={{ margin: 0 }}>Not Yet Reached</Tag>
            ) : isHalted ? (
              <Tag color="default" style={{ margin: 0 }}>Process Halted</Tag>
            ) : (
              <Tag color={TAG_COLOR[step.status] || "default"} style={{ margin: 0 }}>
                {TAG_LABEL[step.status] || step.status}
              </Tag>
            )}
          </div>

          {/* Row 2: Designation + Emp Code */}
          {step.approver_designation && (
            <div style={{ color: "#8c8c8c", fontSize: 12, marginTop: 3 }}>
              {step.approver_designation}
              {step.approver_emp_code && ` • ${step.approver_emp_code}`}
            </div>
          )}

          {/* Row 3: Completed On */}
          {step.completed_on && (
            <div style={{ color: "#595959", fontSize: 12, marginTop: 5 }}>
              <Text strong style={{ fontSize: 11, color: "#595959" }}>
                {step.status === "Separated" ? "Separated On: "
                  : step.status === "Extend" ? "Extended On: "
                    : "Confirmed On: "}
              </Text>
              {dayjs(step.completed_on).format("DD MMM YYYY [at] hh:mm A")}
            </div>
          )}

          {/* Row 4: Remarks */}
          {step.remarks && (
            <div style={{ marginTop: 5, fontSize: 13 }}>
              <Text strong>Remark: </Text>
              <Text>{step.remarks}</Text>
            </div>
          )}

          {/* Row 5: Extended To date */}
          {step.new_probation_end_date && (
            <div style={{ marginTop: 4, fontSize: 13 }}>
              <Text strong style={{ color: "#1890ff" }}>Extended to: </Text>
              <Text style={{ color: "#1890ff" }}>
                {new Date(step.new_probation_end_date).toLocaleDateString("en-GB")}
              </Text>
            </div>
          )}

        </div>
      ),
    };
  });

  // ── PDF Export ──────────────────────────────────────────────────────────
  // Builds a standalone PDF report of the probation review (employee details
  // + full approval hierarchy/timeline) using jsPDF + jspdf-autotable.
  // Requires: npm install jspdf jspdf-autotable
  const handleDownloadPDF = () => {
    try {
      setPdfLoading(true);

      const doc = new jsPDF({ unit: "pt", format: "a4" });
      const pageWidth = doc.internal.pageSize.getWidth();
      const marginX = 40;
      let cursorY = 50;

      // Title
      doc.setFontSize(16);
      doc.setTextColor(14, 73, 123); // #0e497b
      doc.setFont(undefined, "bold");
      doc.text("Probation Review Report", pageWidth / 2, cursorY, { align: "center" });

      cursorY += 10;
      doc.setDrawColor(14, 73, 123);
      doc.line(marginX, cursorY, pageWidth - marginX, cursorY);
      cursorY += 25;

      // Employee Details block
      const overallStatus = (employee.overall_status || "").trim();
      const myStep = allSteps.find((s) => s.approver === myEmployeeId);

      const detailRows = [
        ["Employee", `${emp?.first_name || ""} ${emp?.last_name || ""} (${emp?.emp_code || "—"})`],
        ["Overall Status", TAG_LABEL[overallStatus] || overallStatus || "—"],
        ...(myStep ? [["Your Status", TAG_LABEL[myStep.status] || myStep.status]] : []),
        ["Organisation", emp?.organisation_name || "—"],
        ["Department", emp?.department_name || "—"],
        ["Designation", emp?.designation_name || "—"],
        ["Reporting To", emp?.reporting_to_name || "—"],
        [
          "Date of Joining",
          emp?.date_of_joining
            ? new Date(emp.date_of_joining).toLocaleDateString("en-GB")
            : "—",
        ],
        [
          "Probation End Date",
          employee.final_probation_end_date
            ? new Date(employee.final_probation_end_date).toLocaleDateString("en-GB")
            : "—",
        ],
        ["Email", emp?.email_id || "—"],
        ["Due Days", dueDaysLabel],
      ];

      autoTable(doc, {
        startY: cursorY,
        margin: { left: marginX, right: marginX },
        body: detailRows,
        theme: "grid",
        styles: { fontSize: 10, cellPadding: 6 },
        columnStyles: {
          0: { fontStyle: "bold", textColor: [85, 85, 85], cellWidth: 160 },
          1: { textColor: [30, 30, 30] },
        },
      });

      cursorY = doc.lastAutoTable.finalY + 20;

      // Re-review cycle banner (if applicable)
      if (isReReviewCycle) {
        doc.setFontSize(10);
        doc.setTextColor(180, 120, 0);
        doc.setFont(undefined, "bold");
        doc.text("Re-Review Cycle — Probation was previously extended", marginX, cursorY);
        cursorY += 14;
        doc.setFont(undefined, "normal");
        doc.setTextColor(60, 60, 60);
        const reasonLines = doc.splitTextToSize(
          `Extension Reason: ${employee.final_remarks}`,
          pageWidth - marginX * 2
        );
        doc.text(reasonLines, marginX, cursorY);
        cursorY += reasonLines.length * 12 + 16;
      }

      // Approval Hierarchy heading
      doc.setFontSize(12);
      doc.setTextColor(14, 73, 123);
      doc.setFont(undefined, "bold");
      doc.text("Approval Hierarchy", marginX, cursorY);
      cursorY += 10;

      if (allSteps.length === 0) {
        doc.setFontSize(10);
        doc.setFont(undefined, "normal");
        doc.setTextColor(120, 120, 120);
        doc.text("No approval steps found for this review.", marginX, cursorY + 14);
      } else {
        const stepRows = allSteps.map((step) => {
          const isCurrentStep = step.is_current_step === true;
          const isNotReached = step.status === "Pending" && !isCurrentStep;
          const isHalted =
            step.remarks &&
            (step.remarks.includes("Process halted") || step.remarks.includes("prior approver"));

          const statusLabel = isCurrentStep
            ? "Awaiting Approval"
            : isNotReached
              ? "Not Yet Reached"
              : isHalted
                ? "Process Halted"
                : TAG_LABEL[step.status] || step.status;

          return [
            String(step.order ?? "—"),
            `${step.approver_name || "—"}${step.approver === myEmployeeId ? " (You)" : ""}`,
            step.approver_designation || "—",
            statusLabel,
            step.completed_on
              ? dayjs(step.completed_on).format("DD MMM YYYY hh:mm A")
              : "—",
            step.new_probation_end_date
              ? new Date(step.new_probation_end_date).toLocaleDateString("en-GB")
              : "—",
            step.remarks || "—",
          ];
        });

        autoTable(doc, {
          startY: cursorY + 10,
          margin: { left: marginX, right: marginX },
          head: [["Step", "Approver", "Designation", "Status", "Completed On", "Extended To", "Remarks"]],
          body: stepRows,
          theme: "striped",
          styles: { fontSize: 8, cellPadding: 5, overflow: "linebreak" },
          headStyles: { fillColor: [14, 73, 123], textColor: 255 },
          columnStyles: {
            0: { cellWidth: 30 },
            6: { cellWidth: 130 },
          },
        });
      }

      // Footer
      const pageCount = doc.internal.getNumberOfPages();
      for (let i = 1; i <= pageCount; i++) {
        doc.setPage(i);
        doc.setFontSize(8);
        doc.setTextColor(150, 150, 150);
        doc.text(
          `Generated on ${dayjs().format("DD MMM YYYY [at] hh:mm A")}`,
          marginX,
          doc.internal.pageSize.getHeight() - 20
        );
        doc.text(
          `Page ${i} of ${pageCount}`,
          pageWidth - marginX,
          doc.internal.pageSize.getHeight() - 20,
          { align: "right" }
        );
      }

      const fileName = `Probation_Review_${(emp?.first_name || "employee")}_${(emp?.last_name || "")}_${dayjs().format("YYYYMMDD")}.pdf`.replace(/\s+/g, "_");
      doc.save(fileName);

      toast.success("PDF downloaded successfully.");
    } catch (err) {
      console.error("PDF generation failed:", err);
      toast.error("Failed to generate PDF.");
    } finally {
      setPdfLoading(false);
    }
  };

  return (
    <>
      <ToastContainer position="top-right" autoClose={2500} hideProgressBar />

      {/* ── Main Modal ── */}
      <Modal
        open={visible}
        onCancel={onClose}
        footer={null}
        width={820}
        destroyOnClose
        title={
          <Row justify="center" align="middle" style={{ position: "relative" }}>
            <Title
              level={5}
              style={{ margin: 0, color: "#0e497b", textAlign: "center" }}
            >
              Probation Review
            </Title>
          </Row>
        }
      >
        {/* Employee Details */}
        <Divider style={{ marginTop: 8, marginBottom: 16 }} />

        {/* Download PDF action */}
        <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 12 }}>
          <Button
            icon={<FilePdfOutlined />}
            loading={pdfLoading}
            onClick={handleDownloadPDF}
            style={{
              color: "#0e497b",
              borderColor: "#0e497b",
              fontWeight: 600,
            }}
          >
            Download PDF
          </Button>
        </div>

        <Descriptions
          column={{ xxl: 2, xl: 2, lg: 2, md: 1, sm: 1, xs: 1 }}
          size="small"
          bordered
          labelStyle={{ fontWeight: 600, color: "#555", fontSize: 13 }}
          contentStyle={{ fontSize: 13 }}
          style={{ marginBottom: 20 }}
        >
          <Descriptions.Item label="Employee">
            <Text strong style={{ color: "#0e497b" }}>
              {emp?.first_name} {emp?.last_name}
            </Text>
            &nbsp;&bull;&nbsp;
            <Text type="secondary">{emp?.emp_code}</Text>
          </Descriptions.Item>
          <Descriptions.Item label="Overall Status">
            <Tag color={TAG_COLOR[(employee.overall_status || "").trim()] || "default"}>
              {TAG_LABEL[(employee.overall_status || "").trim()] || (employee.overall_status || "").trim()}
            </Tag>
          </Descriptions.Item>
          {(() => {
            const myStep = allSteps.find(s => s.approver === myEmployeeId);
            return myStep ? (
              <Descriptions.Item label="Status">
                <Tag color={TAG_COLOR[myStep.status] || "default"}>
                  {TAG_LABEL[myStep.status] || myStep.status}
                </Tag>
              </Descriptions.Item>
            ) : null;
          })()}

          <Descriptions.Item label="Organisation">
            {emp?.organisation_name || "—"}
          </Descriptions.Item>
          <Descriptions.Item label="Department">
            {emp?.department_name || "—"}
          </Descriptions.Item>

          <Descriptions.Item label="Designation">
            {emp?.designation_name || "—"}
          </Descriptions.Item>
          <Descriptions.Item label="Reporting To">
            {emp?.reporting_to_name || "—"}
          </Descriptions.Item>

          <Descriptions.Item label="Date of Joining">
            {emp?.date_of_joining
              ? new Date(emp.date_of_joining).toLocaleDateString("en-GB")
              : "—"}
          </Descriptions.Item>
          <Descriptions.Item label="Probation End Date">
            <Text strong style={{ color: "#0e497b" }}>
              {employee.final_probation_end_date
                ? new Date(employee.final_probation_end_date).toLocaleDateString(
                  "en-GB"
                )
                : "—"}
            </Text>
          </Descriptions.Item>

          <Descriptions.Item label="Email">
            {emp?.email_id || "—"}
          </Descriptions.Item>
          <Descriptions.Item label="Due Days">
            <Tag color={dueDaysColor} style={{ margin: 0 }}>
              {dueDaysLabel}
            </Tag>
          </Descriptions.Item>
        </Descriptions>

        {/* Action Panel */}
        {canEdit && activeStepData && (
          <>
            <Divider orientation="left" style={{ color: "#0e497b", fontWeight: 600 }}>
              Remarks
            </Divider>

            <Input.TextArea
              rows={3}
              placeholder="Enter your mandatory review remarks..."
              value={remarks}
              onChange={(e) => setRemarks(e.target.value)}
              style={{ marginBottom: 16 }}
            />

            <div style={{ display: "flex", gap: 12, flexWrap: "wrap", marginBottom: 8 }}>
              <Button
                type="primary"
                loading={confirmLoading}
                onClick={() => handleDecision("Confirm")}
                style={{
                  backgroundColor: "#4CAF50",
                  borderColor: "#4CAF50",
                  fontWeight: 600,
                }}
              >
                Confirm
              </Button>

              <Button
                type="primary"
                onClick={() => handleDecision("Extend")}
                style={{
                  backgroundColor: "#faad14",
                  borderColor: "#faad14",
                  fontWeight: 600,
                }}
              >
                Extend
              </Button>

              <Button
                danger
                type="primary"
                loading={separateLoading}
                onClick={() => handleDecision("Separated")}
                style={{ fontWeight: 600 }}
              >
                Separate
              </Button>
            </div>
          </>
        )}

        {/* Info when no action available */}
        {!canEdit && employee.overall_status === "Pending" && (
          <Alert
            message={(() => {
              const myStep = allSteps.find((s) => s.approver === myEmployeeId);
              const currentStep = allSteps.find((s) => s.is_current_step === true);
              if (myStep && myStep.status !== "Pending") {
                return "You have already submitted your decision for this review.";
              }
              if (currentStep && currentStep.approver !== myEmployeeId) {
                return `Awaiting action from ${currentStep.approver_name} (Step ${currentStep.order}).`;
              }
              return "Awaiting previous approver to take action before your turn.";
            })()}
            type="info"
            showIcon
            style={{ marginBottom: 14 }}
          />
        )}

        {/* Show context when overall status is Extend (extended but not yet re-reviewed) */}
        {employee.overall_status === "Extend" && (
          <Alert
            message={`Probation was extended to ${employee.final_probation_end_date
                ? new Date(employee.final_probation_end_date).toLocaleDateString("en-GB")
                : "—"
              }. Awaiting re-review cycle to begin.`}
            type="warning"
            showIcon
            style={{ marginBottom: 14 }}
          />
        )}

        {/* ⚠️ Re-Review Context Banner — shown to TL when record was extended by HOD/HR */}
        {isReReviewCycle && (
          <Alert
            type="warning"
            showIcon
            style={{ marginBottom: 16, borderRadius: 8 }}
            message={
              <span style={{ fontWeight: 600, fontSize: 13 }}>
                ⚠️ Re-Review Cycle — This employee's probation was previously extended
              </span>
            }
            description={
              <div style={{ marginTop: 6 }}>
                <div style={{ fontSize: 13, marginBottom: 4 }}>
                  <strong>Extension Reason:</strong>{" "}
                  <span style={{ color: "#444" }}>{employee.final_remarks}</span>
                </div>
                <div style={{ fontSize: 12, color: "#666" }}>
                  <strong>Current End Date:</strong>{" "}
                  {employee.final_probation_end_date
                    ? new Date(employee.final_probation_end_date).toLocaleDateString("en-GB")
                    : "—"}
                </div>
              </div>
            }
          />
        )}

        {/* Approval Timeline */}
        <Divider orientation="left" style={{ color: "#0e497b", fontWeight: 600 }}>
          Approval Hierarchy
        </Divider>

        {allSteps.length === 0 ? (
          <Alert
            message="No approval steps found for this review."
            type="warning"
            showIcon
          />
        ) : (
          <div style={{ paddingLeft: 6, marginTop: 16 }}>
            <Timeline items={timelineItems} style={{ marginBottom: 0 }} />
          </div>
        )}
      </Modal>

      {/* ── Extend Modal ── */}
      <Modal
        open={extendModalVisible}
        title={
          <Title level={5} style={{ margin: 0, color: "#0e497b" }}>
            Extend Probation Period
          </Title>
        }
        onCancel={() => setExtendModalVisible(false)}
        onOk={handleExtendSubmit}
        okText="Confirm Extension"
        confirmLoading={submitExtendLoading}
        okButtonProps={{
          style: { backgroundColor: "#0e497b", borderColor: "#0e497b" },
          disabled: !newProbationDate || !remarks.trim(),
        }}
        width={460}
      >
        <p style={{ color: "#555", marginBottom: 14 }}>
          Choose an extension duration from the current probation end date (
          <strong style={{ color: "#0e497b" }}>
            {employee.final_probation_end_date
              ? new Date(employee.final_probation_end_date).toLocaleDateString("en-GB")
              : "Not set"}
          </strong>
          ):
        </p>

        <div
          style={{
            display: "flex",
            justifyContent: "center",
            gap: 10,
            marginBottom: 18,
            flexWrap: "wrap",
          }}
        >
          {[30, 60].map((days) => {
            // Calculate from the current probation end date, not today
            const baseDate = employee.final_probation_end_date
              ? dayjs(employee.final_probation_end_date)
              : dayjs();
            const target = baseDate.add(days, "day").format("YYYY-MM-DD");
            const selected = newProbationDate === target;
            return (
              <Button
                key={days}
                type={selected ? "primary" : "default"}
                style={
                  selected
                    ? { backgroundColor: "#0e497b", borderColor: "#0e497b" }
                    : {}
                }
                onClick={() => setNewProbationDate(target)}
              >
                +{days} Days
              </Button>
            );
          })}
        </div>

        {newProbationDate && (
          <div
            style={{
              textAlign: "center",
              padding: "10px 16px",
              background: "#f0f5fb",
              borderRadius: 8,
              marginBottom: 14,
              fontWeight: 500,
            }}
          >
            New Probation End Date:&nbsp;
            <strong style={{ color: "#0e497b" }}>
              {dayjs(newProbationDate).format("DD/MM/YYYY")}
            </strong>
          </div>
        )}

        {!remarks.trim() && (
          <Alert
            message="Please fill in your remarks in the action panel before extending."
            type="warning"
            showIcon
          />
        )}
      </Modal>
    </>
  );
}


























